import type { IconName } from "@/components/Icons";
import type { ScoreBreakdown, SignalType, SignalTypeFilter } from "./types";

/**
 * Signal-type presentation + score-composition helpers (Feed restyle).
 *
 * Component-free so it can be shared by the mock fixtures, the dormant live
 * adapter, and the card/tooltip/filter components without tripping
 * react-refresh's only-export-components rule.
 */

/** Per-type display metadata: the leading card icon + a singular aria label. */
export const SIGNAL_TYPE_META: Record<
  SignalType,
  { label: string; icon: IconName }
> = {
  rfp: { label: "RFP", icon: "document" },
  hiring: { label: "Hiring", icon: "users" },
  m_and_a: { label: "M&A", icon: "merge" },
  transformation: { label: "Transformation", icon: "transform" },
  leadership: { label: "Leadership", icon: "briefcase" },
  market_intel: { label: "Market Intel", icon: "trend" },
  target_account: { label: "Target Account", icon: "target" },
};

/** Ordered filter chips (spec): All + the seven signal types. */
export const SIGNAL_TYPE_FILTERS: { key: SignalTypeFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "rfp", label: "RFPs" },
  { key: "hiring", label: "Hiring" },
  { key: "m_and_a", label: "M&A" },
  { key: "transformation", label: "Transformation" },
  { key: "leadership", label: "Leadership" },
  { key: "market_intel", label: "Market Intel" },
  { key: "target_account", label: "Target Accts" },
];

/**
 * Best-effort mapping from a live `signal_type` string to the UI taxonomy.
 * Dormant-path only (the mock assigns SignalType directly). Unknown values fall
 * back to market intel rather than throwing so a live row never blanks a card.
 */
export function mapSignalType(raw: string | null): SignalType {
  const s = (raw ?? "").toLowerCase();
  if (s.includes("rfp") || s.includes("procure") || s.includes("tender")) return "rfp";
  if (s.includes("hir") || s.includes("talent") || s.includes("headcount")) return "hiring";
  // "ma" is the get_signal_feed canonical value for M&A; keep the descriptive
  // synonyms too so both the live taxonomy and free-text rows map correctly.
  if (
    s === "ma" ||
    s.includes("m&a") ||
    s.includes("m_and_a") ||
    s.includes("merger") ||
    s.includes("acqui") ||
    s.includes("divest")
  )
    return "m_and_a";
  if (s.includes("transform") || s.includes("restructur") || s.includes("turnaround"))
    return "transformation";
  if (s.includes("leader") || s.includes("cfo") || s.includes("executive") || s.includes("appoint"))
    return "leadership";
  if (s.includes("target")) return "target_account";
  return "market_intel";
}

function summaryFor(
  signalWeight: number,
  capabilityFit: number,
  recency: number,
  isTargetAccount: boolean,
): string {
  const drivers: ReadonlyArray<readonly [string, number]> = [
    ["a high-signal event", signalWeight],
    ["strong capability fit", capabilityFit],
    ["recent activity", recency],
  ];
  const top = drivers.reduce((a, b) => (b[1] > a[1] ? b : a));
  const lead = `Driven mostly by ${top[0]}`;
  return isTargetAccount ? `${lead}, on a named target account.` : `${lead}.`;
}

/**
 * One-line rationale for a score composition. Shared by the mock (which derives
 * the breakdown from the score) and the live adapter (which receives the
 * server's `score_breakdown` projection and only needs the summary line).
 */
export function scoreSummary(b: {
  signalWeight: number;
  capabilityFit: number;
  recency: number;
  targetAccountBonus: number;
}): string {
  return summaryFor(
    b.signalWeight,
    b.capabilityFit,
    b.recency,
    b.targetAccountBonus > 0,
  );
}

/**
 * Deterministic score composition. Components sum exactly to `score`:
 * signalWeight (~50%), capabilityFit (~32%), recency (remainder), plus a fixed
 * target-account bonus. Mock in Phase D; the live projection replaces this.
 */
export function breakdownForScore(
  score: number,
  isTargetAccount: boolean,
): ScoreBreakdown {
  const targetAccountBonus = isTargetAccount ? Math.min(8, score) : 0;
  const base = score - targetAccountBonus;
  const signalWeight = Math.round(base * 0.5);
  const capabilityFit = Math.round(base * 0.32);
  const recency = Math.max(0, base - signalWeight - capabilityFit);
  return {
    signalWeight,
    capabilityFit,
    recency,
    targetAccountBonus,
    summary: summaryFor(signalWeight, capabilityFit, recency, isTargetAccount),
  };
}
